import { useState, useEffect } from "react";
import { api } from "../api";

function EquityCurve({ equity = [], color = "#00E5C0" }) {
  if (equity.length < 2) return null;
  const W = 300, H = 60, PAD = 2;
  const vals = equity.map(e => e.v);
  const mn = Math.min(...vals), mx = Math.max(...vals), rng = mx - mn || 1;
  const pts = vals.map((v, i) =>
    `${((i / (vals.length - 1)) * (W - PAD * 2) + PAD).toFixed(1)},${(H - PAD - ((v - mn) / rng) * (H - PAD * 2)).toFixed(1)}`
  );
  const path = pts.map((p, i) => (i === 0 ? `M${p}` : `L${p}`)).join(" ");
  const fill = `${path} L${W - PAD},${H - PAD} L${PAD},${H - PAD} Z`;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`eq-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#eq-${color.replace("#","")})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function SimCard({ sim }) {
  if (!sim) return (
    <div style={{ background:"#0A0C10", border:"1px solid #1C2030", borderRadius:8,
                  padding:"16px", textAlign:"center", color:"#2A3040", fontSize:10 }}>
      Simulation non disponible
    </div>
  );
  const c = sim.pnl_pct >= 0 ? "#00E5C0" : "#E03030";
  return (
    <div style={{ background:"#0A0C10", border:`1px solid ${c}33`,
                  borderRadius:8, padding:"16px" }}>
      <div style={{ fontSize:8, color:"#2A3040", letterSpacing:2, marginBottom:8 }}>
        SIMULATION {sim.window?.toUpperCase()}
      </div>
      <div style={{ fontSize:28, fontWeight:700, color:c, fontFamily:"IBM Plex Mono" }}>
        {sim.pnl_pct >= 0 ? "+" : ""}{sim.pnl_pct?.toFixed(2)}%
      </div>
      <div style={{ fontSize:11, color:c, marginBottom:8 }}>
        {sim.pnl_usd >= 0 ? "+" : ""}{sim.pnl_usd?.toFixed(2)} USD
      </div>
      <EquityCurve equity={sim.equity_curve} color={c} />
      <div style={{ display:"flex", gap:10, marginTop:8, fontSize:9, color:"#555E6E" }}>
        {sim.hit_sl       && <span style={{color:"#E03030"}}>⚠ SL touché</span>}
        {sim.hit_trailing && <span style={{color:"#F59E0B"}}>📈 Trailing fermé</span>}
        {!sim.hit_sl && !sim.hit_trailing && <span style={{color:"#2A3040"}}>Position ouverte</span>}
      </div>
    </div>
  );
}

function ScoreDetail({ details = {} }) {
  const ITEMS = [
    ["audit",              "Audit",          details.audit],
    ["tvl",                "TVL > $1M",      details.tvl],
    ["age_days",           "Âge > 6 mois",   details.age_days],
    ["liquidity_locked",   "Liquidité",      details.liquidity_locked],
    ["fdv_mc_ratio",       "FDV/MC < 5",     details.fdv_mc_ratio],
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {ITEMS.map(([key, label, item]) => {
        if (!item) return null;
        const ok = item.ok;
        const c = ok === true ? "#00E5C0" : ok === false ? "#E03030" : "#555E6E";
        const val = item.value !== undefined ? ` (${typeof item.value === "number" ? item.value.toFixed(1) : item.value})` : "";
        return (
          <div key={key} style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ color:c, fontSize:10 }}>{ok === true ? "✓" : ok === false ? "✗" : "—"}</span>
            <span style={{ color:"#8892A4", fontSize:10 }}>{label}{val}</span>
            <span style={{ marginLeft:"auto", fontSize:9, color:c }}>{item.pts !== undefined ? `+${item.pts}pts` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function DetailView({ id, onBack }) {
  const [sig, setSig] = useState(null);

  useEffect(() => {
    api.getSignal(id).then(setSig);
  }, [id]);

  if (!sig) return (
    <div style={{ textAlign:"center", padding:"60px", color:"#2A3040", animation:"pulse 1.5s infinite" }}>
      Chargement...
    </div>
  );

  const sims = sig.simulations || [];
  const c = sig.security_score >= 70 ? "#00E5C0" : sig.security_score >= 45 ? "#F59E0B" : "#E03030";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, animation:"fadeIn .3s ease" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onBack} style={{
          background:"transparent", color:"#555E6E", border:"1px solid #1C2030",
          borderRadius:6, padding:"6px 12px", fontSize:10, cursor:"pointer",
        }}>← Retour</button>
        <span style={{ fontSize:18, fontWeight:700, color:"#E0E4F0" }}>{sig.symbol}</span>
        <span style={{ fontSize:12, color:"#555E6E" }}>{sig.name}</span>
        <span style={{ marginLeft:"auto", fontSize:10,
          background: sig.trend_signal==="BUY" ? "#00E5C011":"transparent",
          color: sig.trend_signal==="BUY" ? "#00E5C0":"#555E6E",
          border:`1px solid ${sig.trend_signal==="BUY"?"#00E5C044":"#1C2030"}`,
          borderRadius:4, padding:"4px 12px", fontWeight:700 }}>
          {sig.trend_signal === "BUY" ? "▲ BUY" : "— HOLD"}
        </span>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* Security score */}
        <div style={{ background:"#0D1117", border:"1px solid #1C2030", borderRadius:10, padding:"20px" }}>
          <div style={{ fontSize:8, color:"#2A3040", letterSpacing:2, marginBottom:16 }}>SCORE SÉCURITÉ</div>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
            <div style={{ fontSize:48, fontWeight:700, color:c, fontFamily:"IBM Plex Mono" }}>
              {sig.security_score}
            </div>
            <div style={{ flex:1, height:6, background:"#1C2030", borderRadius:3 }}>
              <div style={{ width:`${sig.security_score}%`, height:"100%",
                background:`linear-gradient(90deg, ${c}88, ${c})`, borderRadius:3 }}/>
            </div>
          </div>
          <ScoreDetail details={sig.security_details} />
          {sig.security_details?.disqualified && (
            <div style={{ marginTop:10, fontSize:9, color:"#E03030",
              background:"#E0303011", border:"1px solid #E0303033",
              borderRadius:4, padding:"6px 10px" }}>
              ⚠ Disqualifié : {sig.security_details.disqualified}
            </div>
          )}
        </div>

        {/* Token info */}
        <div style={{ background:"#0D1117", border:"1px solid #1C2030", borderRadius:10, padding:"20px" }}>
          <div style={{ fontSize:8, color:"#2A3040", letterSpacing:2, marginBottom:16 }}>INFORMATIONS</div>
          {[
            ["Prix actuel",  `$${sig.price_current?.toLocaleString(undefined, {maximumFractionDigits:6}) ?? "—"}`],
            ["Market Cap",   sig.market_cap ? `$${(sig.market_cap/1e6).toFixed(1)}M` : "—"],
            ["Volume 24h",   sig.volume_24h  ? `$${(sig.volume_24h/1e6).toFixed(1)}M`  : "—"],
            ["Chain",        sig.chain],
            ["Détecté le",   sig.detected_at ? new Date(sig.detected_at).toLocaleDateString("fr-FR") : "—"],
            ["Prix détection", sig.price_at_detection ? `$${sig.price_at_detection?.toLocaleString(undefined,{maximumFractionDigits:6})}` : "—"],
          ].map(([k, v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between",
              padding:"6px 0", borderBottom:"1px solid #111520" }}>
              <span style={{ color:"#555E6E", fontSize:10 }}>{k}</span>
              <span style={{ color:"#C8CDD8", fontSize:10, fontWeight:600 }}>{v}</span>
            </div>
          ))}
          {/* Buy button — Phase 3 */}
          <button disabled title="Disponible en Phase 3" style={{
            marginTop:16, width:"100%", padding:"10px",
            background:"#1C2030", color:"#2A3040",
            border:"1px solid #1C2030", borderRadius:6,
            fontSize:11, cursor:"not-allowed", fontFamily:"IBM Plex Mono",
          }}>
            🔒 Acheter (Phase 3)
          </button>
        </div>
      </div>

      {/* Simulations */}
      <div>
        <div style={{ fontSize:8, color:"#2A3040", letterSpacing:2, marginBottom:10 }}>
          SIMULATIONS P&L (entrée ${sig.price_at_detection?.toFixed(4) ?? "—"} · SL 2% · Trailing 5%)
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
          {["1j","7j","30j"].map(w => (
            <SimCard key={w} sim={sims.find(s => s.window === w)} />
          ))}
        </div>
      </div>
    </div>
  );
}
